import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import FavoritesService from '../services/favoritesService';

/**
 * Favorites Context
 * Provides global favorites state and functions
 */
const FavoritesContext = createContext();

/**
 * Custom hook to use the FavoritesContext
 * @returns {Object} Favorites context value
 */
export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

/**
 * FavoritesProvider component
 * Wraps the app to provide favorites context
 */
export const FavoritesProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [favoritesIds, setFavoritesIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Load favorites from server
   */
  const loadFavorites = useCallback(async () => {
    if (!isAuthenticated) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await FavoritesService.getFavorites();
      const favoritesData = response.data.favorites || [];

      setFavorites(favoritesData);

      const ids = favoritesData.map((fav) => fav._id);
      setFavoritesIds(ids);

      FavoritesService.saveFavoritesToStorage(ids);
    } catch (error) {
      console.error('Error loading favorites:', error);
      setError(error.message);

      const localFavorites = FavoritesService.getFavoritesFromStorage();
      setFavoritesIds(localFavorites);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  /**
   * Initialize favorites when user logs in
   */
  useEffect(() => {
    if (isAuthenticated && user) {
      loadFavorites();
    } else {
      setFavorites([]);
      setFavoritesIds([]);
      FavoritesService.clearFavoritesFromStorage();
    }
  }, [isAuthenticated, user, loadFavorites]);

  /**
   * Add property to favorites
   * @param {string} propertyId - Property ID to add
   * @param {Object} propertyData - Optional property data for immediate UI update
   */
  const addToFavorites = async (propertyId, propertyData = null) => {
    if (!isAuthenticated) {
      throw new Error('You must be logged in to add favorites');
    }

    // Optimistic update
    setFavoritesIds(prev => [...prev, propertyId]);
    if (propertyData) {
      setFavorites(prev => [...prev, propertyData]);
    }

    try {
      await FavoritesService.addToFavorites(propertyId);
      
      // Update local storage
      const updatedIds = [...favoritesIds, propertyId];
      FavoritesService.saveFavoritesToStorage(updatedIds);
      
      // Reload to get complete data if we didn't have it
      if (!propertyData) {
        await loadFavorites();
      }
      
    } catch (error) {
      // Revert optimistic update on error
      setFavoritesIds(prev => prev.filter(id => id !== propertyId));
      if (propertyData) {
        setFavorites(prev => prev.filter(fav => fav._id !== propertyId));
      }
      
      console.error('Error adding to favorites:', error);
      throw error;
    }
  };

  /**
   * Remove property from favorites
   * @param {string} propertyId - Property ID to remove
   */
  const removeFromFavorites = async (propertyId) => {
    if (!isAuthenticated) {
      throw new Error('You must be logged in to manage favorites');
    }

    // Store current state for potential rollback
    const previousIds = [...favoritesIds];
    const previousFavorites = [...favorites];

    // Optimistic update
    setFavoritesIds(prev => prev.filter(id => id !== propertyId));
    setFavorites(prev => prev.filter(fav => fav._id !== propertyId));

    try {
      await FavoritesService.removeFromFavorites(propertyId);
      
      // Update local storage
      const updatedIds = favoritesIds.filter(id => id !== propertyId);
      FavoritesService.saveFavoritesToStorage(updatedIds);
      
    } catch (error) {
      // Revert optimistic update on error
      setFavoritesIds(previousIds);
      setFavorites(previousFavorites);
      
      console.error('Error removing from favorites:', error);
      throw error;
    }
  };

  /**
   * Toggle favorite status of a property
   * @param {string} propertyId - Property ID to toggle
   * @param {Object} propertyData - Optional property data
   */
  const toggleFavorite = async (propertyId, propertyData = null) => {
    const isFavorited = isFavorite(propertyId);
    
    if (isFavorited) {
      await removeFromFavorites(propertyId);
    } else {
      await addToFavorites(propertyId, propertyData);
    }
  };

  /**
   * Check if property is favorited
   * @param {string} propertyId - Property ID to check
   * @returns {boolean} True if favorited
   */
  const isFavorite = (propertyId) => {
    return favoritesIds.includes(propertyId);
  };

  /**
   * Get favorites count
   * @returns {number} Number of favorites
   */
  const getFavoritesCount = () => {
    return favoritesIds.length;
  };

  /**
   * Clear all favorites (for logout)
   */
  const clearFavorites = () => {
    setFavorites([]);
    setFavoritesIds([]);
    setError(null);
    FavoritesService.clearFavoritesFromStorage();
  };

  /**
   * Refresh favorites from server
   */
  const refreshFavorites = useCallback(async () => {
    await loadFavorites();
  }, [loadFavorites]);

  const value = {
    // State
    favorites,
    favoritesIds,
    isLoading,
    error,
    
    // Actions
    addToFavorites,
    removeFromFavorites,
    toggleFavorite,
    isFavorite,
    getFavoritesCount,
    clearFavorites,
    refreshFavorites,
    loadFavorites
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};

export default FavoritesContext; 